/**
 * Undo one row of history, whoever made it (RFC 0313 3d).
 *
 * `undoAgentEdit` only took back a session's change. This takes back any row:
 * an agent's change, a run of a person's typing, or a change to an app record.
 * An undo is a NEW change stamped `reverts`, never a rewind, so it is itself a
 * row in history and can be undone in turn.
 *
 * PLACEABLE ONCE, OR NOTHING. Text is found by what the row added plus the
 * context either side, and undo refuses when that appears zero times or more
 * than once. A record field undoes only if it still holds exactly what the row
 * wrote. An added list item undoes only if it is there exactly once. Undoing a
 * list deletion is refused: restore (3e) puts a whole earlier state back. Every
 * check runs before anything is written, so a row undoes whole or not at all.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { Automerge as A } from '@automerge/automerge-repo/slim'
import { changeNote, locateAgentEdit } from '../session/agent-edits'
import {
  changeRow,
  type FieldChange,
  type HistoryChange,
  label as itemName,
  sessionDocumentChanges,
} from './history'
import { touchCatalog } from './mutations'
import { readDocumentContent } from './reads'
import {
  sessionsHolding,
  tellUndone,
  type UndoneBy,
  undoneSummary,
} from './told-undone'
import type { DocumentDoc } from './types'

export type ChangeStatus =
  /** Undo would take it back. */
  | 'live'
  /** An undo naming a change in this row exists. */
  | 'undone'
  /** Somebody has since changed what it wrote, so undo would revert their work. */
  | 'changed'
  /** Undo does not take this kind of change back (a list deletion, a restore). */
  | 'unsupported'

export type UndoChangeResult =
  | { ok: true; hash: string | null; row: HistoryChange }
  | {
      ok: false
      reason: 'not-found' | 'already-undone' | 'changed' | 'unsupported'
    }

/** A plain JSON copy of `data`, so comparisons see strings, not Automerge text. */
function plainData(doc: unknown): Record<string, unknown> | undefined {
  const d = (doc as { data?: unknown } | null)?.data
  if (!d || typeof d !== 'object') return undefined
  return JSON.parse(JSON.stringify(d)) as Record<string, unknown>
}

function at(obj: unknown, path: string[]): unknown {
  let cur: unknown = obj
  for (const k of path) {
    if (!cur || typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[k]
  }
  return cur
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/** Where a row stands against the document as it is now. Pure. */
export function changeStatus(doc: unknown, row: HistoryChange): ChangeStatus {
  if (row.reverted) return 'undone'
  // A restore is an ordinary change and undoes like one. A merge spans many
  // documents and a pull request, so it is not taken back one row at a time.
  if (row.merges) return 'unsupported'
  if (row.text) {
    if (!locateAgentEdit(readDocumentContent(doc), row.text)) return 'changed'
  }
  if (row.fields) {
    const data = plainData(doc)
    for (const f of row.fields) {
      const s = fieldStatus(data, f)
      if (s !== 'live') return s
    }
    if (!removalsFor(doc, row.fields)) return 'changed'
  }
  return 'live'
}

function fieldStatus(
  data: Record<string, unknown> | undefined,
  f: FieldChange,
): ChangeStatus {
  // A change to `data` as a whole is the record coming into being (or
  // going): there is no field to put back, so undo would only claim it did.
  if (!f.path) return 'unsupported'
  const path = f.path.split('.')
  if (f.kind === 'value') {
    return same(at(data, path), f.after) ? 'live' : 'changed'
  }
  if (f.removed.length) return 'unsupported'
  // Each added item must be there exactly once: `removalsFor` checks that.
  return Array.isArray(at(data, path)) ? 'live' : 'changed'
}

/**
 * Undo a row, named by any change in it (a row's `hash` is its last). Writes the
 * reverse as one change stamped `reverts`, and returns the undo's own hash.
 */
export async function undoChange(
  repo: Repo,
  projectId: string,
  documentId: string,
  hash: string,
  opts: {
    authorId?: string
    /** Who undid it, for the event the change's session is told (3f). */
    by?: UndoneBy
  } = {},
): Promise<UndoChangeResult> {
  const handle = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const doc = handle.doc()
  const row = changeRow(doc, hash)
  if (!row) return { ok: false, reason: 'not-found' }
  const status = changeStatus(doc, row)
  if (status === 'undone') {
    // The undo landed before; its notice may not have (an interrupted retry).
    // Telling again is safe: each session is told at most once.
    await tellAbout(repo, projectId, documentId, row, opts)
    return { ok: false, reason: 'already-undone' }
  }
  if (status !== 'live') return { ok: false, reason: status }

  const textAt = row.text
    ? locateAgentEdit(readDocumentContent(doc), row.text)
    : null
  const listRemovals = removalsFor(doc, row.fields ?? []) ?? []

  const now = Date.now()
  handle.change(
    (d) => {
      if (row.text && textAt) {
        A.splice(
          d as A.Doc<unknown>,
          ['content'],
          textAt.from,
          textAt.to - textAt.from,
          row.text.removed,
        )
      }
      for (const f of row.fields ?? []) {
        if (f.kind !== 'value') continue
        setAt(d as unknown as Record<string, unknown>, f.path, f.before)
      }
      // Highest index first, so earlier removals do not shift later ones.
      for (const { path, index } of listRemovals) {
        const list = at((d as { data?: unknown }).data, path) as unknown[]
        list.splice(index, 1)
      }
      ;(d as { updatedAt?: number }).updatedAt = now
    },
    {
      message: changeNote({
        reverts: row.hash,
        ...(opts.authorId ? { authorId: opts.authorId } : {}),
      }),
    },
  )
  await touchCatalog(repo, projectId, documentId, now)
  await tellAbout(repo, projectId, documentId, row, opts)
  const heads = A.getHeads(handle.doc() as A.Doc<unknown>)
  return { ok: true, hash: heads.length === 1 ? (heads[0] ?? null) : null, row }
}

/** Tell the change's session and every session holding the document (3f). */
async function tellAbout(
  repo: Repo,
  projectId: string,
  documentId: string,
  row: HistoryChange,
  opts: { authorId?: string; by?: UndoneBy },
): Promise<void> {
  const holders = await sessionsHolding(repo, projectId, documentId)
  await tellUndone(
    repo,
    [...(row.sessionId ? [row.sessionId] : []), ...holders],
    {
      documentId,
      reverts: row.hash,
      by: opts.by ?? { id: opts.authorId ?? 'user' },
      summary: undoneSummary(row),
    },
  )
}

/**
 * The list items a row added, as indexes in the document now, highest first.
 * Null when one of them is missing or cannot be told apart.
 *
 * BY IDENTITY, NOT BY NAME. An item that is an object is found by its
 * Automerge object id, recorded when the row was read: a later item with the
 * same title is a different item, and undo must not delete it (0313
 * adversarial review, finding 2). A plain value has no identity beyond itself,
 * so it is matched by value and must appear exactly once.
 */
function removalsFor(
  doc: unknown,
  fields: FieldChange[],
): { path: string[]; index: number }[] | null {
  const data = (doc as { data?: unknown } | null)?.data
  const out: { path: string[]; index: number }[] = []
  for (const f of fields) {
    if (f.kind !== 'list') continue
    const path = f.path ? f.path.split('.') : []
    const list = at(data, path)
    if (!Array.isArray(list)) return null
    const items = list as unknown[]
    const taken = new Set<number>()
    for (let n = 0; n < f.added.length; n++) {
      const id = f.addedIds?.[n] ?? null
      let hits: number[]
      if (id) {
        hits = items
          .map((item, i) => (objectId(item) === id ? i : -1))
          .filter((i) => i >= 0 && !taken.has(i))
      } else {
        const name = f.added[n]
        hits = items
          .map((item, i) =>
            typeof item !== 'object' && itemName(item) === name ? i : -1,
          )
          .filter((i) => i >= 0 && !taken.has(i))
      }
      if (hits.length !== 1) return null
      const hit = hits[0] as number
      taken.add(hit)
      out.push({ path, index: hit })
    }
  }
  return out.sort((a, b) => b.index - a.index)
}

function objectId(item: unknown): string | null {
  if (!item || typeof item !== 'object') return null
  try {
    return A.getObjectId(item as A.Doc<unknown>) ?? null
  } catch {
    return null
  }
}

/** Put a value at a dotted path inside `data`, or delete it when undefined. */
function setAt(d: Record<string, unknown>, path: string, value: unknown) {
  const keys = path.split('.')
  let cur = d.data as Record<string, unknown> | undefined
  if (!cur) return
  for (const k of keys.slice(0, -1)) {
    const next = cur[k]
    if (!next || typeof next !== 'object') return
    cur = next as Record<string, unknown>
  }
  const last = keys[keys.length - 1] as string
  if (value === undefined) delete cur[last]
  else cur[last] = value
}

export interface UndoSessionResult {
  undone: number
  refused: number
}

/**
 * Undo every document change one session made, newest first (RFC 0313 3h).
 * Each row refuses on its own terms, and the result says how many undid and
 * how many were refused. It never touches code.
 */
export async function undoSessionChanges(
  repo: Repo,
  projectId: string,
  sessionId: string,
  documentIds: string[],
  opts: { authorId?: string; by?: UndoneBy } = {},
): Promise<UndoSessionResult> {
  const rows: { documentId: string; hash: string; time: number }[] = []
  for (const documentId of documentIds) {
    const handle = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
    await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
    for (const r of sessionDocumentChanges(handle.doc(), sessionId)) {
      if (!r.reverted) rows.push({ documentId, hash: r.hash, time: r.time })
    }
  }
  rows.sort((a, b) => b.time - a.time)
  let undone = 0
  let refused = 0
  for (const r of rows) {
    const out = await undoChange(repo, projectId, r.documentId, r.hash, opts)
    if (out.ok) undone++
    else refused++
  }
  return { undone, refused }
}
